import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Grid,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  GetApp as FetchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { loadLabHistory, loadLabResults, labhistoryLoad, loadPatientLabResultsToday, loadPharmacyInfo } from '../BaseURL';
import LabReportDialog from './LabReportDialog';
import { ErrorMessage } from '../Message';
import { generateLabReportHTML } from './LabReportTemplate';

// Row component with expand/collapse functionality
const LabHistoryRow = ({ row, onViewReport, onViewResults, onEditResults, onViewDateResults }) => {
  const [open, setOpen] = useState(false);
  const [expandedData, setExpandedData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    if (!open && expandedData.length === 0) {
      setLoading(true);
      try {
        const response = await axios.get(`${labhistoryLoad}/${row.id}`);
        
        if (response.data.success) {
          setExpandedData(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching patient lab history:', error);
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          transition: 'background-color 0.2s ease',
          ...(open && {
            bgcolor: 'primary.lighter',
            '&:hover': { bgcolor: 'primary.lighter' }
          })
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              transition: 'transform 0.25s ease, background-color 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              bgcolor: open ? 'primary.main' : 'transparent',
              color: open ? 'white' : 'inherit',
              '&:hover': {
                bgcolor: open ? 'primary.dark' : 'action.hover'
              }
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.id || '-'}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.name || '-'}</TableCell>
        <TableCell>
          <Chip
            label={row.gender || '-'}
            size="small"
            variant="outlined"
            sx={{ minWidth: 60, fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>{row.age || '-'}</TableCell>
        <TableCell>
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontFamily: 'monospace',
              bgcolor: 'primary.lighter',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.85rem'
            }}
          >
            {row.path_no || '-'}
          </Typography>
        </TableCell>
        <TableCell>{row.dat ? new Date(row.dat).toLocaleDateString() : '-'}</TableCell>
        <TableCell>{row.test_by || '-'}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <IconButton
              color="success"
              onClick={() => onViewReport(row)}
              title="View Full Report"
              size="small"
              sx={{
                bgcolor: 'success.lighter',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'success.light',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
            >
              <AssessmentIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => onViewDateResults(row)}
              title="View Results for This Date"
              size="small"
              sx={{
                bgcolor: 'primary.lighter',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'primary.light',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
            >
              <AssignmentIcon fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 2,
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'grey.50',
                borderLeft: '4px solid',
                borderLeftColor: 'primary.main',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.04)'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <AssignmentIcon sx={{ color: 'primary.main' }} />
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  Lab History for {row.name}
                </Typography>
                {!loading && expandedData.length > 0 && (
                  <Chip
                    label={`${expandedData.length} record${expandedData.length === 1 ? '' : 's'}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : expandedData.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No additional lab records found for this patient.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
                >
                  <Table size="small" aria-label="patient lab history">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Path Number</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Test Category</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Test Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Tested By</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expandedData.map((historyRow, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{
                            transition: 'background-color 0.15s ease',
                            '&:nth-of-type(even)': { bgcolor: 'grey.50' }
                          }}
                        >
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem'
                              }}
                            >
                              {historyRow.path_no}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {historyRow.cat || historyRow.category || '-'}
                          </TableCell>
                          <TableCell>
                            {historyRow.dat ? new Date(historyRow.dat).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{historyRow.test_by || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={historyRow.status || 'Completed'}
                              color="success"
                              size="small"
                              variant="filled"
                              sx={{ fontWeight: 600, minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                color="success"
                                onClick={() => onViewReport(historyRow)}
                                title="View Report"
                                size="small"
                                sx={{
                                  bgcolor: 'success.lighter',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'success.light',
                                    transform: 'translateY(-1px)',
                                    boxShadow: 1
                                  }
                                }}
                              >
                                <AssessmentIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                color="primary"
                                onClick={() => onViewDateResults(historyRow)}
                                title="View Results for This Date"
                                size="small"
                                sx={{
                                  bgcolor: 'primary.lighter',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'primary.light',
                                    transform: 'translateY(-1px)',
                                    boxShadow: 1
                                  }
                                }}
                              >
                                <AssignmentIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LabHistory = ({ onEditResults }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [labHistoryData, setLabHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedPathNo, setSelectedPathNo] = useState('');
  const [selectedResults, setSelectedResults] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  // New state for LabReportDialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Load company info on mount
  React.useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyData();
  }, []);

  const handleFetchData = async () => {
    if (!fromDate || !toDate) {
      ErrorMessage('Error', 'Please select both from date and to date');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      ErrorMessage('Error', 'From date cannot be greater than to date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(loadLabHistory, {
        fromDate,
        toDate
      });

      if (response.data.success) {
        setLabHistoryData(response.data.results);
      } else {
        setLabHistoryData([]);
      }
    } catch (error) {
      console.error('Error fetching lab history:', error);
      setLabHistoryData([]);
      alert('Error fetching lab history data');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  
  const handleViewDateLabResults = async (rowData) => {
    console.log('🔵 [LAB HISTORY] View date results clicked:', { rowData });
    
   
    try {
      // Extract the date from the row data
      const testDate = rowData.dat;
      const patientId = rowData.id;
      
       console.log(testDate)
      if (!testDate) {
        alert('No test date available for this record.');
        return;
      }

      const response = await fetch(loadPatientLabResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: patientId,
          date: testDate  // Pass the specific date instead of using today
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Create patient object for the report
        const patient = {
          id: rowData.id,
          fullName: rowData.name,
          age: rowData.age,
          gender: rowData.gender
        };

        // Get user info from localStorage (similar to MyPatients component)
        const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

        // Generate HTML report and open in new window
        const htmlContent = generateLabReportHTML(data.reportData, patient, userInfo, companyInfo);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
      } else {
        const formattedDate = new Date(testDate).toLocaleDateString();
        alert(`No lab results found for ${formattedDate}.`);
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching date lab results:', error);
      alert('Error fetching lab results: ' + error.message);
    }
  };

  const handleViewReport = async (rowData) => {
    console.log('🔵 [LAB HISTORY] View report clicked:', { rowData });
    
    const patient = {
      id: rowData.id,
      fullName: rowData.name,
      age: rowData.age,
      gender: rowData.gender
    };

    setSelectedPatient(patient);
    setReportDialogOpen(true);
    setReportLoading(true);
    setReportData({});

    try {
      const patientId = rowData.id;
      const testCategory = rowData.cat || rowData.category || 'General';
      const date = rowData.dat;
      const pathNo = rowData.path_no;

      console.log('🔵 [LAB HISTORY] Fetching full report with:', {
        patientId,
        testCategory,
        date,
        pathNo
      });

      const url = `${loadLabResults(patientId, testCategory, date)}?labNo=${pathNo}`;
      console.log('🔵 [LAB HISTORY] Request URL:', url);

      const response = await axios.get(url);
      
      console.log('✅ [LAB HISTORY] Response:', response.data);

      if (response.data.success && response.data.results.length > 0) {
        const formattedData = {};
        
        response.data.results.forEach(result => {
          const resultDate = date;
          const category = result.category || testCategory;
          
          if (!formattedData[resultDate]) {
            formattedData[resultDate] = {};
          }
          
          if (!formattedData[resultDate][category]) {
            formattedData[resultDate][category] = [];
          }
          
          formattedData[resultDate][category].push({
            parameter: result.parameterName,
            result: result.result,
            unit: result.unit,
            range: result.range,
            flag: result.flag || 'normal',
            entered_by: result.entered_by
          });
        });

        console.log('✅ [LAB HISTORY] Formatted report data:', formattedData);
        setReportData(formattedData);
      } else {
        console.log('⚠️ [LAB HISTORY] No results found for report');
        setReportData({});
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching lab report:', error);
      console.error('Error details:', error.response?.data);
      setReportData({});
      alert('Error fetching lab report: ' + (error.response?.data?.message || error.message));
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setReportData({});
    setSelectedPatient(null);
  };

  const handleViewResults = async (pathNo, rowData) => {
    console.log('🔵 [LAB HISTORY] View results clicked:', { pathNo, rowData });
    
    setSelectedPathNo(pathNo);
    setSelectedRowData(rowData);
    setResultsLoading(true);
    setResultsDialogOpen(true);

    try {
      const patientId = rowData?.id;
      const testCategory = rowData?.cat || rowData?.category || 'General';
      const date = rowData?.dat;
      
      console.log('🔵 [LAB HISTORY] Fetching results with:', {
        patientId,
        testCategory,
        date,
        pathNo
      });

      const url = `${loadLabResults(patientId, testCategory, date)}?labNo=${pathNo}`;
      console.log('🔵 [LAB HISTORY] Request URL:', url);

      const response = await axios.get(url);
      
      console.log('✅ [LAB HISTORY] Response:', response.data);

      if (response.data.success && response.data.results.length > 0) {
        const groupedResults = response.data.results.reduce((acc, result) => {
          const category = result.category || testCategory;
          if (!acc[category]) {
            acc[category] = {
              testName: category,
              patientName: rowData?.name,
              age: rowData?.age,
              gender: rowData?.gender,
              testDate: date,
              enteredBy: result.entered_by,
              generalComment: response.data.generalComment,
              parameters: []
            };
          }
          acc[category].parameters.push(result);
          return acc;
        }, {});

        const formattedResults = Object.values(groupedResults);
        
        console.log('✅ [LAB HISTORY] Formatted results:', formattedResults);
        setSelectedResults(formattedResults);
      } else {
        console.log('⚠️ [LAB HISTORY] No results found');
        setSelectedResults([]);
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching lab results:', error);
      console.error('Error details:', error.response?.data);
      setSelectedResults([]);
      alert('Error fetching lab results: ' + (error.response?.data?.message || error.message));
    } finally {
      setResultsLoading(false);
    }
  };

  const handleCloseResultsDialog = () => {
    setResultsDialogOpen(false);
    setSelectedPathNo('');
    setSelectedResults([]);
  };

  const filteredData = labHistoryData
    .filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (!searchTerm) return 0;
      
      const search = searchTerm.toLowerCase();
      
      const getMatchPriority = (item) => {
        const id = item.id?.toLowerCase() || '';
        const name = item.name?.toLowerCase() || '';
        const pathNo = item.path_no?.toLowerCase() || '';
        
        if (id === search || name === search || pathNo === search) return 0;
        if (id.startsWith(search) || name.startsWith(search) || pathNo.startsWith(search)) return 1;
        return 2;
      };
      
      return getMatchPriority(a) - getMatchPriority(b);
    });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        Lab History
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={5}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, ID, or path number…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={2}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleFetchData}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FetchIcon fontSize="small" />}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { boxShadow: 2 }
              }}
            >
              {loading ? 'Fetching…' : 'Fetch'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Lab History Results ({filteredData.length} records)
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : labHistoryData.length === 0 ? (
            <Alert severity="info">
              {fromDate && toDate ? 'No lab history found for the selected date range.' : 'Please select a date range and click "Fetch Data" to load lab history.'}
            </Alert>
          ) : (
            <>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: (theme) =>
                          `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                      }}
                    >
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, width: 56 }} />
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Patient ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Patient Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Gender</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Age</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Path Number</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Test Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Tested By</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((row, index) => (
                      <LabHistoryRow
                        key={index}
                        row={row}
                        onViewReport={handleViewReport}
                        onViewResults={handleViewResults}
                        onEditResults={onEditResults}
                        onViewDateResults={handleViewDateLabResults}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <LabReportDialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        reportData={reportData}
        reportLoading={reportLoading}
        selectedPatient={selectedPatient}
      />

      <Dialog
        open={resultsDialogOpen}
        onClose={handleCloseResultsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600 }}>
          Lab Results for Path Number: {selectedPathNo}
        </DialogTitle>
        <DialogContent>
          {resultsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : selectedResults.length === 0 ? (
            <Alert severity="info">No results found for this path number.</Alert>
          ) : (
            <Box>
              {selectedResults.map((test, testIndex) => (
                <Card key={testIndex} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>
                        {test.testName}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          if (onEditResults) {
                            onEditResults({
                              patientId: selectedRowData?.id,
                              pathNo: selectedPathNo,
                              testName: test.testName,
                              patientName: test.patientName,
                              testDate: test.testDate
                            });
                          }
                          handleCloseResultsDialog();
                        }}
                      >
                        Edit Results
                      </Button>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Patient:</Typography>
                        <Typography variant="body1">{test.patientName}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Age/Gender:</Typography>
                        <Typography variant="body1">{test.age} / {test.gender}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Test Date:</Typography>
                        <Typography variant="body1">{new Date(test.testDate).toLocaleDateString()}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Entered By:</Typography>
                        <Typography variant="body1">{test.enteredBy}</Typography>
                      </Grid>
                    </Grid>

                    {test.parameters && test.parameters.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Range</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {test.parameters.map((param, paramIndex) => (
                              <TableRow key={paramIndex}>
                                <TableCell>{param.parameterName}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{param.result}</TableCell>
                                <TableCell>{param.unit}</TableCell>
                                <TableCell>{param.range}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={param.flag}
                                    color={param.flag === 'normal' ? 'success' : 'warning'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="warning">No parameter results available for this test.</Alert>
                    )}

                    {test.generalComment && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">General Comment:</Typography>
                        <Typography variant="body1">{test.generalComment}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabHistory;